import styled from '@emotion/styled';
import { media } from '../../styles/breakpoints';

export const STAGE_ROW_HEIGHT = 44; // px

// From this width the stage selector sits in the top bar beside the instance
// chip and the stage row shows the stage's tabs with their labels. Below it the
// selector starts the stage row, followed by the stage's hamburger menu and the
// tabs as icons (tablets run workshops, so the selector never goes into a menu).
// Measured with the longest chip; Ylläpito is in its hamburger below 1500 px.
export const TOP_STAGES_MIN = 1050; // px

// The stage row under the top bar. Scrolls sideways on narrow screens instead
// of collapsing, so facilitators on a tablet keep one-tap access to the tabs.
export const StageRow = styled.nav`
  display: flex;
  align-items: center;
  gap: 12px;
  height: ${STAGE_ROW_HEIGHT}px;
  box-sizing: border-box;
  padding: 0 24px;
  background: rgba(10, 10, 16, 0.85);
  border-bottom: 1px solid #222;
  backdrop-filter: blur(8px);
  overflow-x: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar { display: none; }

  ${media.sm} {
    padding: 0 14px;
    gap: 8px;
  }

  @media print { display: none; }
`;

// Segmented control: one button per stage.
export const StageSelector = styled.div`
  display: inline-flex;
  flex-shrink: 0;
  padding: 2px;
  gap: 2px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid #2a2a2a;
`;

export const StageButton = styled.button`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  border: none;
  border-radius: 8px;
  background: ${p => (p.$active ? '#0099ff' : 'transparent')};
  color: ${p => (p.$active ? '#fff' : '#999')};
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.02em;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s ease, color 0.15s ease;

  &:hover { color: #fff; background: ${p => (p.$active ? '#0099ff' : 'rgba(255, 255, 255, 0.06)')}; }

  svg { opacity: 0.85; }

  /* The stage the instance is in (from its status) */
  &::after {
    content: '';
    display: ${p => (p.$current ? 'block' : 'none')};
    position: absolute;
    top: 3px;
    right: 4px;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${p => (p.$active ? '#fff' : '#4ade80')};
  }

  ${media.sm} {
    padding: 5px 9px;
    span { display: none; }
  }
`;

export const StageTabs = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
`;

export const StageTab = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border: none;
  border-bottom: 2px solid ${p => (p.$active ? '#0099ff' : 'transparent')};
  border-radius: 0;
  background: transparent;
  color: ${p => (p.$active ? '#fff' : '#888')};
  font-size: 13px;
  font-weight: ${p => (p.$active ? 600 : 500)};
  cursor: pointer;
  white-space: nowrap;

  &:hover { color: #fff; }

  svg { opacity: 0.7; }

  /* With the stage selector in this row, every stage's tabs go icon-only */
  @media (max-width: ${TOP_STAGES_MIN - 1}px) { .label { display: none; } }
`;

export const RowDivider = styled.span`
  width: 1px;
  height: 20px;
  flex-shrink: 0;
  background: #333;
`;
